import { useState } from 'react';
import type { ProvidedJobPostsResponse, AllianceFeeRequest } from '../types/ProvidedJobPost';
import type { AllianceFeeFormValues } from '../schemas/allianceFeeSchema';
import AllianceFeeForm from './AllianceFeeForm';
import useUpdateAllianceFee from '../api/useUpdateAllianceFee';
import { showSuccessToast, showErrorToast } from '@/features/agent/job_change_processes/lib/toast';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/Button';

type Props = {
  // Template 側で jobPost が確定してから渡すため undefined は含まない
  jobPost: ProvidedJobPostsResponse;
};

/**
 * アライアンスタブのルートコンポーネント。
 * - 社外秘に関する注意書きを表示する
 * - AllianceFeeForm にフォームの描画を委譲する
 * - 「設定なし」で既存レコードがある場合は削除確認ダイアログを表示してから送信する
 */
export default function AllianceFeeSection({ jobPost }: Props) {
  const updateAllianceFee = useUpdateAllianceFee(jobPost.id);
  const isSaving = updateAllianceFee.isPending;

  // 削除確認ダイアログの表示状態
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  // ダイアログ確認後に送信するためペイロードを一時保持する
  const [pendingPayload, setPendingPayload] = useState<AllianceFeeRequest>(null);

  // フォームの値から API に送るペイロードを組み立てる。
  // values は zodResolver(allianceFeeSchema) を通過済みなので、
  // rate モードでは fee_rate、amount モードでは fee_amount が必ず数値文字列として入っている。
  const buildPayload = (values: AllianceFeeFormValues): AllianceFeeRequest => {
    if (values.mode === 'none') {
      // mode が none かつ disabled も false のときのみ「全部解除」として null を送る
      // disabled だけ true のときは fee_type=null + disabled=true でレコード保持する
      if (!values.disabled) {
        return null;
      }
      return {
        fee_type: null,
        fee_rate: null,
        fee_amount: null,
        note: values.note?.trim() || null,
        disabled: true,
      };
    }
    if (values.mode === 'rate') {
      return {
        fee_type: 'rate',
        fee_rate: parseFloat(values.fee_rate),
        fee_amount: null,
        note: values.note?.trim() || null,
        disabled: values.disabled,
      };
    }
    // amount モード
    return {
      fee_type: 'amount',
      fee_rate: null,
      fee_amount: Number(values.fee_amount),
      note: values.note?.trim() || null,
      disabled: values.disabled,
    };
  };

  // 実際に API 送信を実行する
  const executeSubmit = async (payload: AllianceFeeRequest) => {
    try {
      await updateAllianceFee.mutateAsync(payload);
      showSuccessToast('アライアンス情報を保存しました');
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : '保存に失敗しました');
    }
  };

  // フォーム送信ハンドラ（zodResolver 通過後に呼ばれる）
  const handleFormSubmit = async (values: AllianceFeeFormValues) => {
    const payload = buildPayload(values);

    // 「設定なし」かつ既存レコードがある場合は削除確認を挟む
    // payload は null（この分岐に入る時点で確定）だが、将来 buildPayload の戻り値が
    // 変わった場合に備えて pendingPayload に payload を渡して意図を明確にする
    if (payload === null && jobPost.alliance_fee !== null) {
      setPendingPayload(payload);
      setIsDeleteConfirmOpen(true);
      return;
    }

    await executeSubmit(payload);
  };

  // 削除確認ダイアログで「削除する」を押したとき
  const handleDeleteConfirm = async () => {
    setIsDeleteConfirmOpen(false);
    await executeSubmit(pendingPayload);
  };

  return (
    <div className="flex flex-col gap-lg rounded-sm bg-white p-6 shadow-layer1">
      <div>
        <h2 className="text-heading-xs">アライアンス成約条件</h2>
        {/* 社外秘に関する注意書き */}
        <div className="mt-3 rounded bg-yellow-50 p-3">
          <p className="text-xs text-yellow-700">
            ここで設定した値（条件の種類・金額・備考）は、求人を公開した際にアライアンス提携エージェントの画面にも表示されます。社外秘の情報は入力しないでください。
          </p>
        </div>
      </div>

      {/* 成約条件編集フォーム */}
      <AllianceFeeForm jobPost={jobPost} onSubmit={handleFormSubmit} isSaving={isSaving} />

      {/* 削除確認ダイアログ（「設定なし」で保存する際に既存レコードがある場合に表示） */}
      <AlertDialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>成約条件の削除</AlertDialogTitle>
          </AlertDialogHeader>
          <div className="p-lg">
            <AlertDialogDescription>
              「設定なし」を選択すると、現在設定されている成約条件が削除されます。この操作は取り消せません。よろしいですか？
            </AlertDialogDescription>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button color="secondary" size="md">
                キャンセル
              </Button>
            </AlertDialogCancel>
            <Button color="danger" size="md" onClick={handleDeleteConfirm} disabled={isSaving}>
              {isSaving ? '削除中...' : '削除する'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
